import { useEffect, useRef, useState } from "react";
import { LoaderCircle, X } from "lucide-react";
import { parsePreview, type PreviewSheet } from "@/lib/api/import";
import type { ImportConfig } from "@/lib/mapper/types";
import { HeaderConfigurator } from "./HeaderConfigurator";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ImportWizardProps = {
  file: File;
  /** "create" (new upload) or "reconfigure" (re-open on existing sheet). */
  mode?: "create" | "reconfigure";
  /** Seed per-sheet config — only respected for the matching sheet in reconfigure mode. */
  initialConfig?: ImportConfig;
  /** Called when the user confirms. Key is the sheet name. */
  onConfirm: (configs: Record<string, ImportConfig>) => void;
  onCancel: () => void;
};

// ---------------------------------------------------------------------------
// Main ImportWizard (modal shell — body is HeaderConfigurator)
// ---------------------------------------------------------------------------

export function ImportWizard({
  file,
  mode = "create",
  initialConfig,
  onConfirm,
  onCancel,
}: ImportWizardProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sheets, setSheets] = useState<PreviewSheet[]>([]);
  const [busy, setBusy] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    parsePreview(file)
      .then((result) => {
        if (controller.signal.aborted) return;
        setSheets(result.sheets);
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Failed to load file preview");
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [file, mode, initialConfig]);

  // Build per-sheet initialConfigs after sheets are loaded (for reconfigure mode)
  const resolvedInitialConfigs: Record<string, ImportConfig> | undefined =
    mode === "reconfigure" && initialConfig && sheets.length > 0
      ? { [sheets[0].name]: initialConfig }
      : undefined;

  async function handleConfirm(configs: Record<string, ImportConfig>) {
    setBusy(true);
    try {
      onConfirm(configs);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-wizard-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 px-4 py-6 backdrop-blur-sm"
    >
      {/* Backdrop close */}
      <button
        type="button"
        aria-label="Cancel import configuration"
        className="absolute inset-0 cursor-default"
        onClick={onCancel}
      />

      <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-[10px] border border-slate-200 bg-white shadow-xl dark:border-border dark:bg-surface">
        {/* Header */}
        <div className="flex flex-shrink-0 items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 dark:border-border">
          <div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
              {mode === "reconfigure" ? "Re-configure import" : "Configure import"}
            </p>
            <h2
              id="import-wizard-title"
              className="mt-1 text-lg font-semibold text-slate-950 dark:text-slate-100"
            >
              {file.name}
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-surface-2 dark:hover:text-slate-100"
            aria-label="Cancel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-500 dark:text-slate-400">
              <LoaderCircle className="h-4 w-4 animate-spin" />
              Analysing file…
            </div>
          )}

          {!loading && error && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/35 dark:text-red-300"
            >
              <strong>Failed to load preview:</strong> {error}
            </div>
          )}

          {!loading && !error && sheets.length === 0 && (
            <div className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
              No sheets were found in this file.
            </div>
          )}

          {!loading && !error && sheets.length > 0 && (
            <HeaderConfigurator
              sheets={sheets}
              initialConfigs={resolvedInitialConfigs}
              mode={mode}
              busy={busy}
              confirmLabel={mode === "reconfigure" ? "Apply" : "Confirm"}
              onConfirm={handleConfirm}
              onCancel={onCancel}
            />
          )}
        </div>
      </div>
    </div>
  );
}
